// src/components/LogActivityModal.jsx
// Unified activity logging modal — used from both AccountDetailPage (ActivitiesTab)
// and OpportunitiesTab. One form, one source of truth.
//
// Props:
//   accountId, accountName
//   opportunityId (optional), opportunityName (optional)
//   onClose()      — called to dismiss the modal
//   onSaved(result) — called after successful save with { activityId, taskId, draftId, calendarId }

import React, { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import './LogActivityModal.css';

const ACTIVITY_TYPES = [
  'Meeting Held',
  'Call',
  'Email Sent',
  'LinkedIn Message',
  'Demo',
  'Proposal Sent',
  'Site Visit',
  'Conference',
  'Follow-up',
  'Other',
];

const EMPTY_FORM = {
  activityType: 'Meeting Held',
  activityDate: new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }),
  contactId:    '',
  name:         '',
  notes:        '',
  outcome:      '',
  followUpType: '',   // '' | 'task' | 'email' | 'meeting'
  followUpDesc: '',
  followUpDue:  '',
  followUpDate: '',   // for meeting
  followUpTime: '',   // for meeting
};

export default function LogActivityModal({
  accountId,
  accountName,
  opportunityId,
  opportunityName,
  onClose,
  onSaved,
}) {
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [contacts, setContacts] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  // Load contacts for this account
  useEffect(() => {
    if (!accountId) return;
    apiFetch(`/api/contacts/by-account?accountId=${accountId}`)
      .then(r => r.json())
      .then(data => { if (data.success) setContacts(data.contacts || []); })
      .catch(() => {});
  }, [accountId]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const selectedContact = contacts.find(c => c.id === form.contactId) || null;

  const handleSave = async () => {
    if (!form.name.trim() || !form.activityType) {
      setError('Description and Activity Type are required.');
      return;
    }
    setError(null);
    setSaving(true);

    try {
      const body = {
        accountId,
        accountName:    accountName   || '',
        opportunityId:  opportunityId || null,
        opportunityName:opportunityName || null,
        contactId:      form.contactId || null,
        contactName:    selectedContact?.name || null,
        name:           form.name.trim(),
        activityType:   form.activityType,
        activityDate:   form.activityDate || null,
        notes:          form.notes.trim()   || null,
        outcome:        form.outcome.trim() || null,
        followUp: form.followUpType ? {
          type:          form.followUpType,
          description:   form.followUpDesc.trim() || null,
          dueDate:       form.followUpDue  || null,
          scheduledDate: form.followUpDate || null,
          scheduledTime: form.followUpTime || null,
        } : null,
      };

      const res  = await apiFetch('/api/activities/log', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();

      if (!data.success) throw new Error(data.error || 'Save failed');

      onSaved && onSaved(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const followUpLabel = {
    task:    '✅ Task created and added to your queue',
    email:   '✉️ Cyrano will draft a follow-up email to Staging',
    meeting: '📅 Meeting will be added to your calendar',
  }[form.followUpType] || null;

  return (
    <div className="lam-overlay" onClick={onClose}>
      <div className="lam-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="lam-header">
          <div className="lam-header-left">
            <span className="lam-header-icon">📋</span>
            <div>
              <div className="lam-header-title">Log Activity</div>
              {(accountName || opportunityName) && (
                <div className="lam-header-sub">
                  {opportunityName ? `💼 ${opportunityName}` : `🏢 ${accountName}`}
                </div>
              )}
            </div>
          </div>
          <button className="lam-close" onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div className="lam-body">

          {/* Row 1: Type + Date */}
          <div className="lam-row">
            <div className="lam-field">
              <label className="lam-label">Activity Type <span className="lam-req">*</span></label>
              <select className="lam-select" value={form.activityType} onChange={e => set('activityType', e.target.value)}>
                {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="lam-field">
              <label className="lam-label">Date</label>
              <input className="lam-input" type="date" value={form.activityDate}
                onChange={e => set('activityDate', e.target.value)} />
            </div>
          </div>

          {/* Contact */}
          <div className="lam-field">
            <label className="lam-label">Contact</label>
            <select className="lam-select" value={form.contactId} onChange={e => set('contactId', e.target.value)}>
              <option value="">— No contact —</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.title ? ` — ${c.title}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="lam-field">
            <label className="lam-label">Description <span className="lam-req">*</span></label>
            <input className="lam-input" type="text" value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder={`e.g., Discovery call with ${selectedContact?.name || 'contact'}`}
              autoFocus />
          </div>

          {/* Notes */}
          <div className="lam-field">
            <label className="lam-label">Notes</label>
            <textarea className="lam-textarea" rows={3} value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="What was discussed, decisions made, intelligence gathered..." />
          </div>

          {/* Outcome */}
          <div className="lam-field">
            <label className="lam-label">Outcome</label>
            <input className="lam-input" type="text" value={form.outcome}
              onChange={e => set('outcome', e.target.value)}
              placeholder="e.g., Agreed to a demo, Request for proposal, No response" />
          </div>

          {/* Follow-up section */}
          <div className="lam-followup-section">
            <div className="lam-followup-label">Follow-up</div>
            <div className="lam-followup-types">
              {[
                { value: '',       label: 'None'    },
                { value: 'task',   label: '✅ Task'  },
                { value: 'email',  label: '✉️ Email' },
                { value: 'meeting',label: '📅 Meeting'},
              ].map(opt => (
                <button
                  key={opt.value}
                  className={`lam-type-btn ${form.followUpType === opt.value ? 'active' : ''}`}
                  onClick={() => set('followUpType', opt.value)}
                  type="button"
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {form.followUpType === 'task' && (
              <div className="lam-followup-fields">
                <div className="lam-row">
                  <div className="lam-field lam-field--grow">
                    <label className="lam-label">Task description</label>
                    <input className="lam-input" type="text" value={form.followUpDesc}
                      onChange={e => set('followUpDesc', e.target.value)}
                      placeholder={`Follow up with ${selectedContact?.name || 'contact'}`} />
                  </div>
                  <div className="lam-field">
                    <label className="lam-label">Due date</label>
                    <input className="lam-input" type="date" value={form.followUpDue}
                      onChange={e => set('followUpDue', e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {form.followUpType === 'email' && (
              <div className="lam-followup-fields">
                <div className="lam-field">
                  <label className="lam-label">What should the email accomplish?</label>
                  <input className="lam-input" type="text" value={form.followUpDesc}
                    onChange={e => set('followUpDesc', e.target.value)}
                    placeholder="e.g., Thank you + share case study on claims modernization" />
                </div>
                <div className="lam-followup-note">
                  Cyrano will draft this based on your activity notes and send it to Staging for your approval.
                </div>
              </div>
            )}

            {form.followUpType === 'meeting' && (
              <div className="lam-followup-fields">
                <div className="lam-field">
                  <label className="lam-label">Meeting title</label>
                  <input className="lam-input" type="text" value={form.followUpDesc}
                    onChange={e => set('followUpDesc', e.target.value)}
                    placeholder={`Follow-up meeting${selectedContact?.name ? ` with ${selectedContact.name}` : ''}`} />
                </div>
                <div className="lam-row">
                  <div className="lam-field">
                    <label className="lam-label">Date</label>
                    <input className="lam-input" type="date" value={form.followUpDate}
                      onChange={e => set('followUpDate', e.target.value)} />
                  </div>
                  <div className="lam-field">
                    <label className="lam-label">Time (Eastern)</label>
                    <input className="lam-input" type="time" value={form.followUpTime}
                      onChange={e => set('followUpTime', e.target.value)} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {error && <div className="lam-error">⚠️ {error}</div>}
        </div>

        {/* Footer */}
        <div className="lam-footer">
          {followUpLabel && !saving && (
            <div className="lam-followup-hint">{followUpLabel}</div>
          )}
          <div className="lam-footer-actions">
            <button className="lam-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="lam-btn-save" onClick={handleSave}
              disabled={saving || !form.name.trim() || !form.activityType}>
              {saving ? 'Saving…' : 'Log Activity'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
